import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { Fragment, PureComponent, ReactNode } from 'react';
import fetchAllMedicalRecordsInADoc, {
    FilesEntity,
    SmartReport,
} from '../api/fetchAllMedicalRecordsInADoc';
import AppLoader from '../components/common/AppLoader';
import RightPopUp from '../components/common/RightPopUp';
import { LabVitalEntity } from '../store/types/prescription';
import { track } from '../tracking';
import { UIDocumentEntity } from './MedicalRecordsRightPanel';
import { ImproveSmartReportDialog } from '../components/common/ImproveSmartReportDialog';
import { WhatIsSmartReportDialog } from '../components/common/WhatIsSmartReportDialog';
import { Popover, notification } from 'antd';
import { captureError } from './captureError';
import { OPTIONS } from '../components/common/MedicalRecordFileTypeSelector';
import MediaViewerNew from '../components/common/MediaViewerNew';

type Props = {
    documentFolder: UIDocumentEntity;
    onClick?: () => void;
    onDelete?: () => void;
    copyToPrescription?: (items: LabVitalEntity[]) => void;
    containerCss?: string;
    showPtName?: boolean;
    isSelected?: boolean;
    hideCopyToPrescription?: boolean;
};

type State = {
    isPopUpVisible?: boolean;
    isFilesInDocLoading?: boolean;
    files?: FilesEntity[] | null;
    smartReport?: SmartReport | null;
    improveSmartReportDialog?: boolean;
    whatIsSmartReportDialog?: boolean;
    notesCount: number;
};

class MedicalRecordNew extends PureComponent<Props, State> {
    state: State = {
        isPopUpVisible: false,
        isFilesInDocLoading: false,
        files: null,
        smartReport: null,
        improveSmartReportDialog: false,
        whatIsSmartReportDialog: false,
        notesCount: 0,
    };

    componentDidMount(): void {
        const { isSelected, documentFolder } = this.props;
        this.onSetNotesCount(documentFolder?.notes || 0);
        if (isSelected) this.onSelect();
    }

    onSelect = (): void => {
        const { onClick, documentFolder } = this.props;

        this.setState({
            isFilesInDocLoading: true,
            isPopUpVisible: true,
        });

        if ('files' in documentFolder) {
            this.setState({
                files: documentFolder.files,
                smartReport: undefined,
                isFilesInDocLoading: false,
            });
        } else {
            fetchAllMedicalRecordsInADoc(documentFolder._pt_oid, documentFolder.document_id)
                .then((res) => {
                    const f = res.files;
                    this.setState({
                        files: f,
                        smartReport: res.smart_report,
                        isFilesInDocLoading: false,
                    });
                })
                .catch((e) => {
                    console.error(e);
                    captureError(e);
                    notification.error({
                        message: 'Failed to fetch medical files',
                        duration: 5,
                        placement: 'topRight',
                    });

                    track('ERROR', {
                        on: 'MedicalViewer#onRecordSelect',
                        what: 'fetchAllMedicalRecordsInADoc',
                        error: e.message,
                    });
                });
        }

        if (onClick) {
            onClick();
        }
    };

    closeMediaViewer = async (): Promise<void> => {
        this.setState({ isPopUpVisible: false });
    };

    improveSmartReportDialogToggler = (): void => {
        track('TOGGLE_DIALOG', {
            while: 'improve-smart-report-dialog',
            value: (!this.state.whatIsSmartReportDialog).toString(),
        });
        this.setState({ improveSmartReportDialog: !this.state.improveSmartReportDialog });
    };

    whatIsSmartReportDialogToggler = (): void => {
        track('TOGGLE_DIALOG', {
            while: 'what-is-smart-report-dialog',
            value: (!this.state.whatIsSmartReportDialog).toString(),
        });
        this.setState({ whatIsSmartReportDialog: !this.state.whatIsSmartReportDialog });
    };

    onCopyToPrescription = this.props.copyToPrescription
        ? (items: LabVitalEntity[]): void => {
              const { documentFolder, copyToPrescription } = this.props;
              const date = moment(
                  documentFolder?.document_date ||
                      (documentFolder?.u_at ? documentFolder?.u_at * 1000 : undefined) ||
                      new Date(),
              ).toISOString();
              copyToPrescription && copyToPrescription(items.map((t) => ({ ...t, date })));
          }
        : undefined;

    onSetNotesCount = (count: number) => {
        this.setState({
            notesCount: count,
        });
    };

    render(): ReactNode {
        const {
            documentFolder,
            onDelete,
            containerCss,
            showPtName,
            isSelected,
            hideCopyToPrescription,
        } = this.props;
        const {
            isPopUpVisible,
            files,
            smartReport,
            isFilesInDocLoading,
            improveSmartReportDialog,
            whatIsSmartReportDialog,
            notesCount,
        } = this.state;

        return (
            <Fragment>
                <AnimatePresence>
                    {isPopUpVisible && (
                        <RightPopUp closeMe={this.closeMediaViewer}>
                            <Fragment>
                                <div
                                    className="flex items-center justify-between px-16 py-12 bg-white"
                                    style={{ height: 60 }}
                                >
                                    <div className="flex items-center text-20 space-x-8">
                                        <div
                                            onClick={this.closeMediaViewer}
                                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 448 512"
                                                className="fill-darwin-neutral-1000 w-16 h-16"
                                            >
                                                <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z" />
                                            </svg>
                                        </div>
                                        <span>
                                            {OPTIONS.find(
                                                (opt) =>
                                                    opt.value ===
                                                    documentFolder?.latest_document_type,
                                            )?.label || ''}
                                        </span>
                                    </div>

                                    <div className="flex items-center space-x-8 ">
                                        {documentFolder.is_smart_report_available && (
                                            <div
                                                className="flex items-center space-x-8 cursor-pointer"
                                                onClick={this.whatIsSmartReportDialogToggler}
                                            >
                                                <div className="flex items-center justify-center w-16 h-16">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="17"
                                                        height="16"
                                                        viewBox="0 0 17 16"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M9.41016 2.79297L10.9336 6.06836L14.209 7.5918C14.4121 7.69336 14.5645 7.89648 14.5645 8.125C14.5645 8.37891 14.4121 8.58203 14.209 8.68359L10.9336 10.207L9.41016 13.4824C9.30859 13.6855 9.10547 13.8125 8.87695 13.8125C8.62305 13.8125 8.41992 13.6855 8.31836 13.4824L6.79492 10.207L3.51953 8.68359C3.31641 8.58203 3.18945 8.37891 3.18945 8.125C3.18945 7.89648 3.31641 7.69336 3.51953 7.5918L6.79492 6.06836L8.31836 2.79297C8.41992 2.58984 8.62305 2.4375 8.87695 2.4375C9.10547 2.4375 9.30859 2.58984 9.41016 2.79297Z"
                                                            fill="#215FFF"
                                                        />
                                                    </svg>
                                                </div>
                                                <p className="caption-1 bold text-darwin-accent-bright-blue-800">
                                                    What’s smart report
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!files || isFilesInDocLoading ? (
                                    <div className="flex flex-1 flex-auto w-full h-full items-center justify-center">
                                        <AppLoader />
                                    </div>
                                ) : (
                                    <div className="flex w-full h-full overflow-hidden">
                                        <MediaViewerNew
                                            files={files || []}
                                            smartReport={smartReport || undefined}
                                            copyToPrescription={this.onCopyToPrescription}
                                            smartReportHandler={
                                                this.improveSmartReportDialogToggler
                                            }
                                            documentFolder={documentFolder}
                                            notesCount={notesCount}
                                            onSetNotesCount={this.onSetNotesCount}
                                            patientId={documentFolder._pt_oid}
                                            hideCopyToPrescription={hideCopyToPrescription}
                                        />
                                    </div>
                                )}
                                <AnimatePresence>
                                    {improveSmartReportDialog && (
                                        <ImproveSmartReportDialog
                                            closeMe={this.improveSmartReportDialogToggler}
                                        />
                                    )}
                                </AnimatePresence>
                                <AnimatePresence>
                                    {whatIsSmartReportDialog && (
                                        <WhatIsSmartReportDialog
                                            closeMe={this.whatIsSmartReportDialogToggler}
                                        />
                                    )}
                                </AnimatePresence>
                            </Fragment>
                        </RightPopUp>
                    )}
                </AnimatePresence>

                <div
                    key={documentFolder.document_id}
                    className={`rounded-8 bg-white p-16 ${containerCss} ${
                        isSelected ? 'border border-darwin-accent-bright-blue-800' : ''
                    }`}
                    style={{ boxShadow: '0px 1px 2px #c6c6c6' }}
                    onClick={this.onSelect}
                >
                    <div className="relative flex items-center justify-center flex-col rounded-t-8 bg-white">
                        <div className="flex w-full items-center justify-between">
                            <div>
                                <p className="text-11 text-darwin-neutral_new-800">
                                    {documentFolder.display_dd ? documentFolder.display_dd : '-'}
                                </p>
                                <span className="text-13 text-darwin-neutral_new-1000 font-700">
                                    {documentFolder?.user_tags?.find((tag) =>
                                        tag?.startsWith('RT#'),
                                    )
                                        ? documentFolder?.user_tags
                                              ?.find((tag) => tag?.startsWith('RT#'))
                                              ?.replace(/^RT#/, '')
                                        : OPTIONS.find(
                                              (o) =>
                                                  o.value === documentFolder.latest_document_type,
                                          )?.label}
                                </span>
                            </div>

                            <div className="flex items-center space-x-8">
                                {notesCount ? (
                                    <div className="flex items-center justify-center space-x-4 bg-darwin-neutral-1000 px-8 py-3 rounded-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                        >
                                            <path
                                                d="M9.93945 1.65625H1.81445C1.58594 1.65625 1.4082 1.85938 1.4082 2.0625V10.1875C1.4082 10.416 1.58594 10.5938 1.81445 10.5938H7.50195V8.5625C7.50195 8.13086 7.85742 7.75 8.31445 7.75H10.3457V2.0625C10.3457 1.85938 10.1426 1.65625 9.93945 1.65625ZM1.81445 11.8125C0.900391 11.8125 0.189453 11.1016 0.189453 10.1875V2.0625C0.189453 1.17383 0.900391 0.4375 1.81445 0.4375H9.93945C10.8281 0.4375 11.5645 1.17383 11.5645 2.0625V7.75V7.90234C11.5645 8.33398 11.3867 8.74023 11.082 9.04492L8.77148 11.3555C8.4668 11.6602 8.06055 11.8125 7.62891 11.8125H7.50195H1.81445ZM5.06445 3.28125C5.06445 3.07812 5.24219 2.875 5.4707 2.875H6.2832C6.48633 2.875 6.68945 3.07812 6.68945 3.28125V4.5H7.9082C8.11133 4.5 8.31445 4.70312 8.31445 4.90625V5.71875C8.31445 5.94727 8.11133 6.125 7.9082 6.125H6.68945V7.34375C6.68945 7.57227 6.48633 7.75 6.2832 7.75H5.4707C5.24219 7.75 5.06445 7.57227 5.06445 7.34375V6.125H3.8457C3.61719 6.125 3.43945 5.94727 3.43945 5.71875V4.90625C3.43945 4.70312 3.61719 4.5 3.8457 4.5H5.06445V3.28125Z"
                                                fill="#E8E8E8"
                                            />
                                        </svg>
                                        <p className="text-white text-13 font-700">{notesCount}</p>
                                    </div>
                                ) : null}
                                {!documentFolder.is_processing &&
                                    documentFolder?.is_smart_report_available && (
                                        <div className="flex items-center justify-center space-x-4 bg-darwin-accent-bright-blue-100 px-8 py-3 rounded-4">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 17 16"
                                                fill="none"
                                            >
                                                <rect
                                                    x="0.876953"
                                                    width="16"
                                                    height="16"
                                                    rx="8"
                                                    fill="#215FFF"
                                                />
                                                <path
                                                    d="M9.32812 3.36328L10.6172 6.13477L13.3887 7.42383C13.5605 7.50977 13.6895 7.68164 13.6895 7.875C13.6895 8.08984 13.5605 8.26172 13.3887 8.34766L10.6172 9.63672L9.32812 12.4082C9.24219 12.5801 9.07031 12.6875 8.87695 12.6875C8.66211 12.6875 8.49023 12.5801 8.4043 12.4082L7.11523 9.63672L4.34375 8.34766C4.17188 8.26172 4.06445 8.08984 4.06445 7.875C4.06445 7.68164 4.17188 7.50977 4.34375 7.42383L7.11523 6.13477L8.4043 3.36328C8.49023 3.19141 8.66211 3.0625 8.87695 3.0625C9.07031 3.0625 9.24219 3.19141 9.32812 3.36328Z"
                                                    fill="white"
                                                />
                                            </svg>
                                            <p className="text-darwin-accent-bright-blue-800 text-13">
                                                Smart
                                            </p>
                                        </div>
                                    )}
                                {!documentFolder.is_processing && documentFolder.is_analyzing && (
                                    <div className="flex items-center space-x-8 bg-darwin-neutral-50 rounded-4 px-8 py-4">
                                        <div className="flex items-center justify-center w-16 h-16">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 576 512"
                                                className="fill-darwin-accent-bright-blue-800 w-12 h-12 animate-spin"
                                            >
                                                <path d="M312 104C312 117.3 301.3 128 288 128C274.7 128 264 117.3 264 104V24C264 10.75 274.7 0 288 0C301.3 0 312 10.75 312 24V104zM312 488C312 501.3 301.3 512 288 512C274.7 512 264 501.3 264 488V408C264 394.7 274.7 384 288 384C301.3 384 312 394.7 312 408V488zM32 256C32 242.7 42.75 232 56 232H136C149.3 232 160 242.7 160 256C160 269.3 149.3 280 136 280H56C42.75 280 32 269.3 32 256zM520 232C533.3 232 544 242.7 544 256C544 269.3 533.3 280 520 280H440C426.7 280 416 269.3 416 256C416 242.7 426.7 232 440 232H520zM469 74.98C478.4 84.35 478.4 99.55 469 108.9L412.5 165.5C403.1 174.9 387.9 174.9 378.5 165.5C369.1 156.1 369.1 140.9 378.5 131.6L435.1 74.98C444.5 65.61 459.6 65.61 469 74.98zM140.9 437C131.5 446.4 116.4 446.4 106.1 437C97.61 427.6 97.61 412.4 106.1 403.1L163.5 346.5C172.9 337.1 188.1 337.1 197.5 346.5C206.9 355.9 206.9 371.1 197.5 380.4L140.9 437zM106.1 74.98C116.4 65.61 131.5 65.61 140.9 74.98L197.5 131.6C206.9 140.9 206.9 156.1 197.5 165.5C188.1 174.9 172.9 174.9 163.5 165.5L106.1 108.9C97.61 99.55 97.61 84.35 106.1 74.98V74.98zM469 403.1C478.4 412.4 478.4 427.6 469 437C459.6 446.4 444.5 446.4 435.1 437L378.5 380.4C369.1 371.1 369.1 355.9 378.5 346.5C387.9 337.1 403.1 337.1 412.5 346.5L469 403.1z"></path>
                                            </svg>
                                        </div>
                                        <p className="text-darwin-accent-bright-blue-800">
                                            Analysing..
                                        </p>
                                    </div>
                                )}

                                {documentFolder.is_processing && (
                                    <div className="flex items-center space-x-8 bg-darwin-neutral-50 rounded-4 px-8 py-4">
                                        <div className="flex items-center justify-center w-16 h-16">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 576 512"
                                                className="fill-darwin-accent-bright-blue-800 w-12 h-12 animate-spin"
                                            >
                                                <path d="M312 104C312 117.3 301.3 128 288 128C274.7 128 264 117.3 264 104V24C264 10.75 274.7 0 288 0C301.3 0 312 10.75 312 24V104zM312 488C312 501.3 301.3 512 288 512C274.7 512 264 501.3 264 488V408C264 394.7 274.7 384 288 384C301.3 384 312 394.7 312 408V488zM32 256C32 242.7 42.75 232 56 232H136C149.3 232 160 242.7 160 256C160 269.3 149.3 280 136 280H56C42.75 280 32 269.3 32 256zM520 232C533.3 232 544 242.7 544 256C544 269.3 533.3 280 520 280H440C426.7 280 416 269.3 416 256C416 242.7 426.7 232 440 232H520zM469 74.98C478.4 84.35 478.4 99.55 469 108.9L412.5 165.5C403.1 174.9 387.9 174.9 378.5 165.5C369.1 156.1 369.1 140.9 378.5 131.6L435.1 74.98C444.5 65.61 459.6 65.61 469 74.98zM140.9 437C131.5 446.4 116.4 446.4 106.1 437C97.61 427.6 97.61 412.4 106.1 403.1L163.5 346.5C172.9 337.1 188.1 337.1 197.5 346.5C206.9 355.9 206.9 371.1 197.5 380.4L140.9 437zM106.1 74.98C116.4 65.61 131.5 65.61 140.9 74.98L197.5 131.6C206.9 140.9 206.9 156.1 197.5 165.5C188.1 174.9 172.9 174.9 163.5 165.5L106.1 108.9C97.61 99.55 97.61 84.35 106.1 74.98V74.98zM469 403.1C478.4 412.4 478.4 427.6 469 437C459.6 446.4 444.5 446.4 435.1 437L378.5 380.4C369.1 371.1 369.1 355.9 378.5 346.5C387.9 337.1 403.1 337.1 412.5 346.5L469 403.1z"></path>
                                            </svg>
                                        </div>
                                        <p className="text-darwin-accent-bright-blue-800">
                                            Awaiting upload..
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="cursor-pointer py-16 h-168 w-full">
                            <img
                                src={
                                    documentFolder.is_processing
                                        ? '/images/mr-analysing.png'
                                        : documentFolder.thumbnail
                                }
                                style={{ objectFit: 'contain' }}
                                className={
                                    !documentFolder.is_processing
                                        ? 'w-full h-full rounded-8'
                                        : 'w-84 h-92 mt-32'
                                }
                            />
                        </div>
                        <div className="flex items-center justify-between w-full">
                            {!!documentFolder?.user_tags?.length ? (
                                <div className="space-x-8 overflow-auto">
                                    {documentFolder?.user_tags?.map((tag) => {
                                        if (tag?.startsWith('RT#')) {
                                            return null;
                                        }
                                        return (
                                            <span
                                                className="px-8 py-4 rounded-4 text-13 whitespace-nowrap"
                                                style={{
                                                    backgroundColor: '#E8E8E8',
                                                }}
                                            >
                                                {tag}
                                            </span>
                                        );
                                    })}
                                </div>
                            ) : (
                                <span className="text-11 text-darwin-neutrals-600">
                                    No Tag added
                                </span>
                            )}

                            {documentFolder.uploaded_by_me && onDelete && (
                                <Popover
                                    content={
                                        <div>
                                            {documentFolder.uploaded_by_me && onDelete && (
                                                <div
                                                    className="text-status-warning flex items-center space-x-12 cursor-pointer"
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        onDelete();
                                                    }}
                                                >
                                                    <span>Delete</span>
                                                    <svg
                                                        viewBox="64 64 896 896"
                                                        focusable="false"
                                                        data-icon="delete"
                                                        width="1.1em"
                                                        height="1.1em"
                                                        fill="rgb(97, 106, 143)"
                                                        aria-hidden="true"
                                                        className="fill-status-warning"
                                                    >
                                                        <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    placement="bottomLeft"
                                    trigger="click"
                                >
                                    <div
                                        className="px-8 cursor-pointer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="4"
                                            height="14"
                                            viewBox="0 0 4 14"
                                            fill="none"
                                        >
                                            <path
                                                d="M1.87695 10.5C2.4082 10.5 2.87695 10.8125 3.1582 11.25C3.43945 11.7188 3.43945 12.3125 3.1582 12.75C2.87695 13.2188 2.4082 13.5 1.87695 13.5C1.31445 13.5 0.845703 13.2188 0.564453 12.75C0.283203 12.3125 0.283203 11.7188 0.564453 11.25C0.845703 10.8125 1.31445 10.5 1.87695 10.5ZM1.87695 5.5C2.4082 5.5 2.87695 5.8125 3.1582 6.25C3.43945 6.71875 3.43945 7.3125 3.1582 7.75C2.87695 8.21875 2.4082 8.5 1.87695 8.5C1.31445 8.5 0.845703 8.21875 0.564453 7.75C0.283203 7.3125 0.283203 6.71875 0.564453 6.25C0.845703 5.8125 1.31445 5.5 1.87695 5.5ZM3.37695 2C3.37695 2.5625 3.06445 3.03125 2.62695 3.3125C2.1582 3.59375 1.56445 3.59375 1.12695 3.3125C0.658203 3.03125 0.376953 2.5625 0.376953 2C0.376953 1.46875 0.658203 1 1.12695 0.71875C1.56445 0.4375 2.1582 0.4375 2.62695 0.71875C3.06445 1 3.37695 1.46875 3.37695 2Z"
                                                fill="#1A1A1A"
                                            />
                                        </svg>
                                    </div>
                                </Popover>
                            )}
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default MedicalRecordNew;
